import { ChevronLeft, ChevronRight, TriangleAlert } from 'lucide-react';
import { useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { OPEN_INTERVIEW_STATUSES, formatClock, interviewEndsAt, zonedDateTimeToUtc, zonedParts, type InterviewSummaryView, type PlannerView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { CellWarning } from '../../../components/editable-table/EditableTable';
import { Segmented } from '../../../components/Segmented';
import { toneCardClass, type AreaTone } from '../../../components/Tone';
import { formatIsoDate, formatTime, formatZoneName } from '../../../lib/format';
import { useNow } from '../../../lib/use-now';
import { dayHeading, dayKey, shortDay } from '../../interviews/interview-ui';
import { usePlannerSave } from '../api/contact-api';
import { FULL_WIDTH, layoutOverlaps, type ColumnPlacement, type TimeSpan } from './calendar-layout';
import styles from './contact.module.css';

// R7 — PLN-02, the planner as a calendar (PL-3): the same rows as the table, a week or a day,
// one colour per interviewer (ADR 0035's area hues), a line at the time now, and the viewer's
// zone. Dragging an interview is editing its date cell: the same save, the same warning before
// anything is saved. A phone gets the days as a list, as SCH-02 does (S19 Q7).

const DAY_MS = 86_400_000;
const FIRST_HOUR = 8;
const LAST_HOUR = 20;
const WORKING_WEEK = 5;
const WEEK = 7;
const SNAP_MINUTES = 15;
/** A pointer that moves less than this is a click, not a drag. */
const DRAG_THRESHOLD_PX = 4;
/** ADR 0035's area hues, in an order that keeps neighbours apart. */
const INTERVIEWER_TONES: readonly AreaTone[] = ['job', 'offer', 'organisation', 'requisition', 'assessment', 'tasks', 'screening', 'admin', 'reports', 'interview', 'dashboard'];

interface CalendarProps {
  readonly jobId: string;
  readonly view: PlannerView;
  readonly zone: string;
  /** The Sunday the week starts on, or the day shown. */
  readonly anchorDay: string | null;
  readonly span: 'week' | 'day';
  readonly onNavigate: (anchorDay: string | null, span: 'week' | 'day') => void;
  readonly onOpen: (interviewId: string) => void;
}

interface Drag {
  readonly id: string;
  readonly day: string;
  readonly minutes: number;
}

interface PendingMove {
  readonly row: InterviewSummaryView;
  readonly drawn: InterviewSummaryView;
  readonly startsAt: string;
  readonly warnings: readonly string[];
}

function addDays(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

function sundayOf(day: string): string {
  return addDays(day, -new Date(`${day}T00:00:00Z`).getUTCDay());
}

function minutesInto(iso: string, zone: string): number {
  const { time } = zonedParts(iso, zone);
  const [hours, minutes] = time.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

/** The shortest a block is drawn, so a short interview still has room for its name. */
const MIN_BLOCK_MINUTES = 30;

const pad = (value: number): string => String(value).padStart(2, '0');
const clockOf = (minutes: number): string => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/** The lead's colour, else the first interviewer's — the same person is always the same colour. */
export function interviewerTones(rows: readonly InterviewSummaryView[]): ReadonlyMap<string, AreaTone> {
  const ids = [...new Set(rows.flatMap((row) => row.panel.map((member) => member.userId)))].sort();
  return new Map(ids.map((id, index) => [id, INTERVIEWER_TONES[index % INTERVIEWER_TONES.length] ?? 'interview']));
}

export interface LegendEntry {
  readonly userId: string;
  readonly fullName: string;
  readonly tone: AreaTone;
}

/** The legend, by name (Job-contact.dc.html); each person keeps the colour `interviewerTones` gave them. */
export function interviewerLegend(rows: readonly InterviewSummaryView[], tones: ReadonlyMap<string, AreaTone>): readonly LegendEntry[] {
  const names = new Map(rows.flatMap((row) => row.panel.map((member): [string, string] => [member.userId, member.fullName])));
  return [...tones.entries()]
    .map(([userId, tone]) => ({ userId, fullName: names.get(userId) ?? '—', tone }))
    .sort((a, b) => a.fullName.localeCompare(b.fullName) || a.userId.localeCompare(b.userId));
}

/** The minutes a block covers on its day, as drawn — what "overlapping" is measured on. */
function spanOf(row: InterviewSummaryView, zone: string): TimeSpan | null {
  if (!row.startsAt) return null;
  const start = minutesInto(row.startsAt, zone);
  return { id: row.id, start, end: start + Math.max(row.durationMinutes, MIN_BLOCK_MINUTES) };
}

function ownerOf(row: InterviewSummaryView): InterviewSummaryView['panel'][number] | null {
  return row.panel.find((member) => member.role === 'lead') ?? row.panel[0] ?? null;
}

export function PlannerCalendar({ jobId, view, zone, anchorDay, span, onNavigate, onOpen }: CalendarProps): ReactNode {
  const now = useNow();
  const save = usePlannerSave(jobId);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [pending, setPending] = useState<PendingMove | null>(null);
  const today = dayKey(new Date(now).toISOString(), zone);
  const start = span === 'day' ? (anchorDay ?? today) : sundayOf(anchorDay ?? today);
  const week = Array.from({ length: WEEK }, (_, index) => addDays(start, index));
  const placed = view.rows.filter((row) => row.startsAt && row.status !== 'cancelled');
  const onDay = (day: string): readonly InterviewSummaryView[] => placed.filter((row) => row.startsAt && dayKey(row.startsAt, zone) === day);
  const isWeekendUsed = week.slice(WORKING_WEEK).some((day) => onDay(day).length > 0);
  const days = span === 'day' ? [start] : isWeekendUsed ? week : week.slice(0, WORKING_WEEK);
  const tones = interviewerTones(view.rows);
  const unplaced = view.rows.filter((row) => !row.startsAt && OPEN_INTERVIEW_STATUSES.includes(row.status)).length;
  const step = span === 'day' ? 1 : WEEK;
  // Geometry, not appearance: how many day columns the grid draws.
  const gridStyle: CSSProperties & Readonly<Record<'--nr-week-days', number>> = { '--nr-week-days': days.length };

  async function move(row: InterviewSummaryView, startsAt: string, isConfirmed: boolean): Promise<void> {
    const drawn = { ...row, startsAt, endsAt: interviewEndsAt(new Date(startsAt), row.durationMinutes).toISOString() };
    setPending(null);
    try {
      await save(row, drawn, { kind: 'time', startsAt }, isConfirmed);
    } catch (error) {
      if (error instanceof CellWarning) setPending({ row, drawn, startsAt, warnings: error.warnings });
    }
  }

  function startDrag(event: ReactPointerEvent<HTMLButtonElement>, row: InterviewSummaryView): void {
    if (event.button !== 0 || !row.startsAt) return;
    const isMovable = view.canEdit && OPEN_INTERVIEW_STATUSES.includes(row.status);
    const origin = { x: event.clientX, y: event.clientY };
    let isDragging = false;
    let target: Drag | null = null;
    const columns = Array.from(gridRef.current?.querySelectorAll<HTMLElement>('[data-day]') ?? []);
    const pointerMinutes = (box: DOMRect, y: number): number => {
      const fraction = Math.min(Math.max((y - box.top) / box.height, 0), 1);
      return FIRST_HOUR * 60 + fraction * (LAST_HOUR - FIRST_HOUR) * 60;
    };
    // Where in the block it was grabbed, so the block moves by what was dragged, not to the pointer.
    const sourceBox = event.currentTarget.parentElement?.getBoundingClientRect() ?? null;
    const grabOffset = sourceBox ? pointerMinutes(sourceBox, event.clientY) - minutesInto(row.startsAt, zone) : 0;
    const targetAt = (x: number, y: number): Drag | null => {
      const column = columns.find((element) => {
        const box = element.getBoundingClientRect();
        return x >= box.left && x <= box.right;
      });
      const day = column?.dataset.day;
      if (!column || !day) return null;
      const raw = pointerMinutes(column.getBoundingClientRect(), y) - grabOffset;
      const minutes = Math.min(Math.max(Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES, FIRST_HOUR * 60), LAST_HOUR * 60 - SNAP_MINUTES);
      return { id: row.id, day, minutes };
    };
    // On the window, not the block: the block is drawn in another day's column while it moves,
    // so the element that was pressed is gone by the time the pointer is released.
    const onPointerMove = (moveEvent: PointerEvent): void => {
      if (!isMovable) return;
      if (!isDragging && Math.hypot(moveEvent.clientX - origin.x, moveEvent.clientY - origin.y) < DRAG_THRESHOLD_PX) return;
      isDragging = true;
      target = targetAt(moveEvent.clientX, moveEvent.clientY);
      setDrag(target);
    };
    const onPointerUp = (upEvent: PointerEvent): void => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      setDrag(null);
      if (!isDragging) {
        if (upEvent.type === 'pointerup') onOpen(row.id);
        return;
      }
      if (upEvent.type !== 'pointerup' || !target || !row.startsAt) return;
      const at = zonedDateTimeToUtc(target.day, clockOf(target.minutes), zone);
      if (!at || at.getTime() === Date.parse(row.startsAt)) return;
      void move(row, at.toISOString(), false);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  }

  return (
    <div className={styles.calendar}>
      <div className={styles.calendarBar}>
        <div className={styles.weekNav}>
          <Button variant="ghost" size="sm" icon={ChevronLeft} aria-label={span === 'day' ? 'Previous day' : 'Previous week'} onClick={() => onNavigate(addDays(start, -step), span)} />
          <span className={styles.weekLabel}>{span === 'day' ? dayHeading(start) : `${formatIsoDate(start)} – ${formatIsoDate(addDays(start, WEEK - 1))}`}</span>
          <Button variant="ghost" size="sm" icon={ChevronRight} aria-label={span === 'day' ? 'Next day' : 'Next week'} onClick={() => onNavigate(addDays(start, step), span)} />
          <Button variant="outline" size="sm" disabled={span === 'day' ? start === today : start === sundayOf(today)} onClick={() => onNavigate(null, span)}>
            {span === 'day' ? 'Today' : 'This week'}
          </Button>
        </div>
        <Segmented label="Show" value={span} options={[{ value: 'week', label: 'Week' }, { value: 'day', label: 'Day' }]} onChange={(next) => onNavigate(next === 'day' ? (week.includes(today) ? today : start) : start, next)} />
        <span className={styles.meta}>Times in {formatZoneName(now, { timeZone: zone })}</span>
      </div>

      {pending && (
        <div className={styles.calendarWarning} role="alert">
          <span className={styles.warningLine}>
            <TriangleAlert size={15} aria-hidden />
            {pending.row.candidateName} to {formatTime(pending.startsAt, { timeZone: zone })}: {pending.warnings.join(' ')} Not saved yet.
          </span>
          <span className={styles.inlineButtons}>
            <Button size="sm" variant="solid" onClick={() => void move(pending.row, pending.startsAt, true)} autoFocus>
              Save anyway
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPending(null)}>
              Cancel
            </Button>
          </span>
        </div>
      )}

      <ul className={styles.legend} aria-label="Interviewers">
        {interviewerLegend(view.rows, tones).map((entry) => (
          <li key={entry.userId} className={styles.legendItem}>
            <span className={styles.legendSwatch} data-tone={entry.tone} aria-hidden />
            {entry.fullName}
          </li>
        ))}
      </ul>

      <div ref={gridRef} className={styles.week} style={gridStyle} role="grid" aria-label={span === 'day' ? `Interviews on ${dayHeading(start)}` : 'Interviews this week'}>
        <div className={styles.weekCorner} />
        {days.map((day) => (
          <div key={day} className={styles.weekHead} role="columnheader" data-today={day === today || undefined}>
            {span === 'day' ? dayHeading(day) : shortDay(day)}
          </div>
        ))}
        <div className={styles.hours} aria-hidden>
          {Array.from({ length: LAST_HOUR - FIRST_HOUR }, (_, index) => (
            <span key={index} className={styles.hour}>
              {formatClock(`${pad(FIRST_HOUR + index)}:00`)}
            </span>
          ))}
        </div>
        {days.map((day) => {
          const shown = placed.filter((row) => (drag?.id === row.id ? drag.day === day : row.startsAt !== null && dayKey(row.startsAt, zone) === day));
          // The block being dragged rides on top at full width; the rest stand side by side where they overlap.
          const layout = layoutOverlaps(shown.filter((row) => row.id !== drag?.id).flatMap((row) => spanOf(row, zone) ?? []));
          return (
            <div key={day} className={styles.dayColumn} role="gridcell" data-day={day} data-today={day === today || undefined}>
              {day === today && <NowLine now={now} zone={zone} />}
              {shown.map((row) => (
                <Block
                  key={row.id}
                  row={row}
                  zone={zone}
                  tone={tones.get(ownerOf(row)?.userId ?? '') ?? 'interview'}
                  drag={drag?.id === row.id ? drag : null}
                  placement={layout.get(row.id) ?? FULL_WIDTH}
                  isMovable={view.canEdit && OPEN_INTERVIEW_STATUSES.includes(row.status)}
                  onPointerDown={startDrag}
                  onOpen={onOpen}
                />
              ))}
            </div>
          );
        })}
      </div>

      <div className={styles.dayList}>
        {days
          .filter((day) => onDay(day).length > 0)
          .map((day) => (
            <section key={day} aria-label={dayHeading(day)}>
              <h3 className={styles.dayHeading}>{dayHeading(day)}</h3>
              <ul className={styles.dayItems}>
                {onDay(day).map((row) => (
                  <li key={row.id}>
                    <button type="button" className={`${toneCardClass(true)} ${styles.dayItem}`} data-tone={tones.get(ownerOf(row)?.userId ?? '') ?? 'interview'} onClick={() => onOpen(row.id)}>
                      <span className={styles.strong}>{row.startsAt ? formatTime(row.startsAt, { timeZone: zone }) : '—'}</span> {row.candidateName} · {row.roundName}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        {days.every((day) => onDay(day).length === 0) && <p className={styles.meta}>Nothing booked {span === 'day' ? 'this day' : 'this week'}.</p>}
      </div>

      {unplaced > 0 && (
        <p className={styles.meta}>
          {unplaced} {unplaced === 1 ? 'interview has' : 'interviews have'} no time yet — set it in the Planner.
        </p>
      )}
    </div>
  );
}

function NowLine({ now, zone }: { readonly now: number; readonly zone: string }): ReactNode {
  const minutes = minutesInto(new Date(now).toISOString(), zone);
  if (minutes < FIRST_HOUR * 60 || minutes > LAST_HOUR * 60) return null;
  // Geometry, not appearance: where now sits in the day, in hour-heights.
  const style: CSSProperties = { top: `calc(var(--nr-week-hour-h) * ${(minutes - FIRST_HOUR * 60) / 60})` };
  return <span className={styles.nowLine} style={style} aria-hidden />;
}

function Block({
  row,
  zone,
  tone,
  drag,
  placement,
  isMovable,
  onPointerDown,
  onOpen,
}: {
  readonly row: InterviewSummaryView;
  readonly zone: string;
  readonly tone: AreaTone;
  readonly drag: Drag | null;
  readonly placement: ColumnPlacement;
  readonly isMovable: boolean;
  readonly onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>, row: InterviewSummaryView) => void;
  readonly onOpen: (interviewId: string) => void;
}): ReactNode {
  if (!row.startsAt) return null;
  const startMinutes = drag ? drag.minutes : minutesInto(row.startsAt, zone);
  const start = Math.max(startMinutes / 60, FIRST_HOUR);
  const end = Math.min(Math.max((startMinutes + row.durationMinutes) / 60, start + MIN_BLOCK_MINUTES / 60), LAST_HOUR);
  // Geometry, not appearance: where the block sits in its day, in hour-heights, and which of its
  // overlap group's columns it takes.
  const style: CSSProperties & Readonly<Record<'--nr-block-col' | '--nr-block-cols', number>> = {
    top: `calc(var(--nr-week-hour-h) * ${start - FIRST_HOUR})`,
    height: `calc(var(--nr-week-hour-h) * ${end - start})`,
    '--nr-block-col': placement.column,
    '--nr-block-cols': placement.columns,
  };
  const time = drag ? `${formatClock(clockOf(drag.minutes))} ${formatZoneName(row.startsAt, { timeZone: zone })}` : formatTime(row.startsAt, { timeZone: zone });
  return (
    <button
      type="button"
      className={`${toneCardClass(true)} ${styles.block}`}
      data-tone={tone}
      data-dragging={drag ? true : undefined}
      data-movable={isMovable || undefined}
      style={style}
      aria-label={`${row.candidateName}, ${row.roundName}, ${time}${isMovable ? '. Drag to move it' : ''}`}
      onPointerDown={(event) => onPointerDown(event, row)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(row.id);
        }
      }}
    >
      <span className={styles.blockName}>{row.candidateName}</span>
      <span className={styles.blockLine}>
        {time} · {row.panel.map((member) => member.fullName).join(', ') || 'No interviewer'}
      </span>
    </button>
  );
}
